import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../Auth/AuthContext';
import useWishlists from '../../hooks/useWishlists';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Bookmark, 
  BookmarkCheck, 
  Plus, 
  Check, 
  X,
  ChevronDown 
} from 'lucide-react';

export default function AddToWishlistButton({ spot, size = "sm", variant = "ghost", className = "" }) {
  const { isAuthenticated } = useAuth();
  const {
    wishlists,
    loading,
    getSpotWishlists,
    isSpotInAnyWishlist,
    getSpotWishlistCount,
    createWishlist,
    addSpotToWishlist,
    removeSpotFromWishlist
  } = useWishlists();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWishlistName, setNewWishlistName] = useState('');
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowCreateForm(false);
        setNewWishlistName('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleClick = (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      // This should be handled by parent component to show auth modal
      return;
    }
    
    setShowDropdown(!showDropdown);
  };

  const handleToggleWishlist = async (wishlistId, e) => {
    e.stopPropagation();
    
    try {
      const spotWishlists = getSpotWishlists(spot.id);
      const isInThisWishlist = spotWishlists.some(w => w.id === wishlistId);
      
      if (isInThisWishlist) {
        await removeSpotFromWishlist(wishlistId, spot.id);
      } else {
        await addSpotToWishlist(wishlistId, spot.id);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      alert('Erreur lors de la modification: ' + error.message);
    }
  };

  const handleCreateWishlist = async (e) => {
    e.stopPropagation();
    
    if (!newWishlistName.trim()) return;
    
    setCreating(true);
    try {
      const newWishlist = await createWishlist({
        name: newWishlistName.trim(),
        description: ''
      });
      
      // Automatically add the spot to the new wishlist
      await addSpotToWishlist(newWishlist.id, spot.id);
      
      setNewWishlistName('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating wishlist:', error);
      alert('Erreur lors de la création: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  if (!spot) return null;

  const spotWishlists = getSpotWishlists(spot.id);
  const isInAnyWishlist = isSpotInAnyWishlist(spot.id);
  const wishlistCount = getSpotWishlistCount(spot.id);

  // Sort wishlists to show the ones containing this spot first
  const sortedWishlists = [...wishlists].sort((a, b) => {
    const aHasSpot = spotWishlists.some(w => w.id === a.id);
    const bHasSpot = spotWishlists.some(w => w.id === b.id);
    
    if (aHasSpot && !bHasSpot) return -1;
    if (!aHasSpot && bHasSpot) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={isInAnyWishlist ? "default" : variant}
        size={size}
        onClick={handleClick}
        className={`flex items-center space-x-1 ${
          isInAnyWishlist 
            ? 'bg-purple-500 hover:bg-purple-600 text-white' 
            : 'hover:bg-purple-50 hover:text-purple-600'
        } ${className}`}
        title={isInAnyWishlist ? `Dans ${wishlistCount} liste${wishlistCount > 1 ? 's' : ''} d'envie` : 'Ajouter à une liste d\'envie'}
      >
        {isInAnyWishlist ? (
          <BookmarkCheck size={16} className="fill-current" />
        ) : (
          <Bookmark size={16} />
        )}
        {size !== "sm" && (
          <span>
            {isInAnyWishlist 
              ? `${wishlistCount} liste${wishlistCount > 1 ? 's' : ''}` 
              : 'Liste d\'envie'
            }
          </span>
        )}
        <ChevronDown size={12} />
      </Button>
      
      {showDropdown && (
        <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64">
          <div className="p-3">
            <div className="text-sm font-medium text-gray-700 mb-3">
              Ajouter "{spot.title}" à :
            </div>
            
            {loading ? (
              <div className="text-center py-2 text-gray-500">Chargement...</div>
            ) : (
              <>
                {sortedWishlists.length > 0 ? (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {/* Wishlists containing this spot (shown first) */}
                    {spotWishlists.length > 0 && (
                      <>
                        <div className="text-xs font-medium text-purple-600 mb-2">
                          ✓ Déjà dans ces listes :
                        </div>
                        {spotWishlists.map((wishlist) => (
                          <div
                            key={wishlist.id}
                            className="flex items-center justify-between p-2 bg-purple-50 rounded border"
                          >
                            <span className="text-sm font-medium text-purple-700">
                              {wishlist.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleToggleWishlist(wishlist.id, e)}
                              className="p-1 h-6 w-6 text-purple-600 hover:text-red-600"
                            >
                              <X size={12} />
                            </Button>
                          </div>
                        ))}
                        
                        {sortedWishlists.length > spotWishlists.length && (
                          <div className="text-xs font-medium text-gray-600 mb-2 mt-3">
                            Autres listes disponibles :
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Other wishlists */}
                    {sortedWishlists
                      .filter(wishlist => !spotWishlists.some(w => w.id === wishlist.id))
                      .map((wishlist) => (
                        <div
                          key={wishlist.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={(e) => handleToggleWishlist(wishlist.id, e)}
                        >
                          <span className="text-sm">{wishlist.name}</span>
                          <Plus size={14} className="text-gray-400" />
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <div className="text-center py-2 text-gray-500 text-sm">
                    Aucune liste d'envie créée
                  </div>
                )}
                
                {/* Create new wishlist section */}
                <div className="border-t pt-3 mt-3">
                  {showCreateForm ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Nom de la nouvelle liste"
                        value={newWishlistName}
                        onChange={(e) => setNewWishlistName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateWishlist(e);
                          }
                        }}
                        className="text-sm"
                        autoFocus
                      />
                      <div className="flex justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCreateForm(false);
                            setNewWishlistName('');
                          }}
                          className="px-3"
                        >
                          Annuler
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateWishlist}
                          disabled={creating || !newWishlistName.trim()}
                          className="px-3"
                        >
                          {creating ? 'Création...' : 'Créer'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateForm(true);
                      }}
                      className="w-full"
                    >
                      <Plus size={14} className="mr-1" />
                      Nouvelle liste d'envie
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}